import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Controller } from 'react-hook-form';

import Input from '../../../common/form/Input';
import Select from '../../../common/form/Select';
import RadioButtons from '../../../common/buttons/RadioButtons';
import Checkbox from '../../../common/form/Checkbox';
import DatePicker from '../../../common/form/DatePicker';
import AutoComplete from '../../../common/form/AutoComplete';
import { InternalFormValues } from './types';
import { stateOptions, airlineOptions } from './constants';

interface TravelInformationStepProps {
	formValues: InternalFormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
}

export default function TravelInformationStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
}: TravelInformationStepProps) {
	return (
		<Box sx={{ py: 2 }}>
			{/* Preferred Airline Details */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Preferred Airline Details
			</Typography>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="homeAirport"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Preferred airport"
								fullWidth
								error={!!errors.homeAirport}
								helperText={errors.homeAirport?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="preferredSeating"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Preferred seating"
								name="preferredSeating"
								options={[
									{ value: 'window', label: 'Window' },
									{ value: 'middle', label: 'Middle' },
									{ value: 'aisle', label: 'Aisle' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="dateOfBirth"
						control={control}
						rules={
							isRequired
								? { required: 'Date of birth is required' }
								: {}
						}
						render={({ field }) => (
							<DatePicker
								{...field}
								label="Date of birth"
								placeholder="MM/DD/YYYY"
								required={isRequired}
								fullWidth
								error={!!errors.dateOfBirth}
								helperText={errors.dateOfBirth?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="tsaPrecheck"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="TSA PreCheck #"
								fullWidth
								error={!!errors.tsaPrecheck}
								helperText={errors.tsaPrecheck?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="globalEntry"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Global Entry #"
								fullWidth
								error={!!errors.globalEntry}
								helperText={errors.globalEntry?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="passportNumber"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Passport #"
								fullWidth
								error={!!errors.passportNumber}
								helperText={errors.passportNumber?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="passportIssuingCountry"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Passport issuing country"
								fullWidth
								error={!!errors.passportIssuingCountry}
								helperText={
									errors.passportIssuingCountry?.message
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="passportExpirationDate"
						control={control}
						render={({ field }) => (
							<DatePicker
								{...field}
								label="Passport expiration date"
								placeholder="MM/DD/YYYY"
								fullWidth
								error={!!errors.passportExpirationDate}
								helperText={
									errors.passportExpirationDate?.message
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="noPassport"
						control={control}
						render={({ field }) => (
							<Checkbox
								items={[
									{
										id: 'noPassport',
										name: 'I do not have a passport',
										checked: field.value,
										handleOnChange: field.onChange,
									},
								]}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="preferredAirlines"
						control={control}
						render={({ field }) => (
							<AutoComplete
								{...field}
								id="preferredAirlines"
								label="Preferred airline"
								options={airlineOptions}
								value={airlineOptions.filter(
									(option) =>
										Array.isArray(field.value) &&
										field.value.includes(option.id)
								)}
								onChange={(event, newValue) => {
									const selectedIds = newValue.map(
										(option) => option.id
									);
									field.onChange(selectedIds);
								}}
								multiple={true}
								placeholder="Select preferred airlines..."
								error={!!errors.preferredAirlines}
								helperText={errors.preferredAirlines?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="deltaMileage"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Delta #"
								fullWidth
								error={!!errors.deltaMileage}
								helperText={errors.deltaMileage?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="unitedMileage"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="United #"
								fullWidth
								error={!!errors.unitedMileage}
								helperText={errors.unitedMileage?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Vehicle Information */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Vehicle Information
			</Typography>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="driversLicenseNumber"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Driver license #"
								fullWidth
								error={!!errors.driversLicenseNumber}
								helperText={
									errors.driversLicenseNumber?.message
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="driversLicenseState"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								label="Driver license state"
								options={stateOptions}
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="noDriversLicense"
						control={control}
						render={({ field }) => (
							<Checkbox
								items={[
									{
										id: 'noDriversLicense',
										name: 'I do not have a driver license',
										checked: field.value,
										handleOnChange: field.onChange,
									},
								]}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="hasVehicle"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Do you have a vehicle?"
								name="hasVehicle"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="willingToUseVehicleForProduction"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Are you willing to use your vehicle for paid production purposes?"
								name="willingToUseVehicleForProduction"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="vehicleType"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="If yes, what is your vehicle type?"
								name="vehicleType"
								options={[
									{ value: 'sedan', label: 'Sedan' },
									{ value: 'suv', label: 'SUV' },
									{ value: 'truck', label: 'Truck' },
									{ value: 'van', label: 'Van' },
								]}
								value={field.value}
								onChange={field.onChange}
								row={false}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="vehicleLocation"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Where is your vehicle based?"
								fullWidth
								error={!!errors.vehicleLocation}
								helperText={errors.vehicleLocation?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
